import { useState, useEffect } from "react";
import { Image, Button } from "antd";
import { PictureOutlined, ReloadOutlined } from "@ant-design/icons";
import { refreshFacebookMessageAttachments } from "@api/services/facebook";
import { useAppDispatch } from "@stores/redux/store";
import { updateMessageUrl } from "@features/messages/messagesSlice";
import { useNotificationContext } from "@hooks/useNotificationContext";
import ImagePreviewModal from "./ImagePreviewModal";
import styles from "./styles.module.less";

interface DefaultImagePlaceholderProps {
  onRetry?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isRefreshing?: boolean;
}

const DefaultImagePlaceholder = ({
  onRetry,
  isRefreshing = false,
}: DefaultImagePlaceholderProps) => (
  <div className={styles.imagePlaceholder}>
    <div className={styles.placeholderIcon}>
      <PictureOutlined style={{ fontSize: 42, color: "#ffffff" }} />
    </div>
    <div className={styles.placeholderContent}>
      <span className={styles.placeholderText}>Hình ảnh đã hết hạn</span>
    </div>
    {onRetry && (
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        className={styles.retryButton}
        size="small"
        onClick={onRetry}
        loading={isRefreshing}
        disabled={isRefreshing}
      >
        Tải lại
      </Button>
    )}
  </div>
);

interface MessageImageProps {
  src: string;
  messageId?: string;
  conversationId?: string;
  pageId?: string | null;
  disablePreview?: boolean;
}

export const MessageImage = ({
  src,
  messageId,
  conversationId,
  pageId,
  disablePreview = false,
}: MessageImageProps) => {
  const { success, error: showError } = useNotificationContext();
  const [imageError, setImageError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const dispatch = useAppDispatch();

  const handleRetry = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!messageId || !pageId) {
      showError("Không tìm thấy thông tin message hoặc page");
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await refreshFacebookMessageAttachments({
        messageId,
        pageId,
      });

      if (response.code === 0) {
        const newUrl = response.data?.newUrl;
        if (newUrl && messageId && conversationId) {
          dispatch(
            updateMessageUrl({
              conversationId,
              messageId,
              newUrl,
            })
          );
          setCurrentSrc(newUrl);
          setImageError(false);
          setRetryKey((prev) => prev + 1);
          success("Đã tải lại hình ảnh thành công");
        } else {
          success("Đang tải lại hình ảnh...");
          setImageError(false);
          setRetryKey((prev) => prev + 1);
        }
      } else {
        showError(response.message || "Tải lại hình ảnh thất bại");
      }
    } catch (error) {
      console.error("Error refreshing message attachments:", error);
      showError("Tải lại hình ảnh thất bại");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (src) {
      setCurrentSrc(src);
    }
  }, [src]);

  if (!currentSrc) {
    return (
      <DefaultImagePlaceholder
        onRetry={handleRetry}
        isRefreshing={isRefreshing}
      />
    );
  }

  const isDataUrl =
    currentSrc.startsWith("data:image") || currentSrc.startsWith("blob:");
  const handleError = () => setImageError(true);
  const handleLoad = () => setImageError(false);

  if (isDataUrl) {
    return (
      <div className={styles.imageWrapper}>
        {imageError ? (
          <DefaultImagePlaceholder
            onRetry={handleRetry}
            isRefreshing={isRefreshing}
          />
        ) : (
          <img
            key={`${currentSrc}-${retryKey}`}
            src={currentSrc}
            alt="Message image"
            className={styles.image}
            onError={handleError}
            onLoad={handleLoad}
          />
        )}
      </div>
    );
  }

  if (imageError) {
    return (
      <div className={styles.imageWrapper} style={{ width: 240, height: 200 }}>
        <DefaultImagePlaceholder
          onRetry={handleRetry}
          isRefreshing={isRefreshing}
        />
      </div>
    );
  }

  return (
    <div className={styles.imageWrapper}>
      <Image
        key={`${currentSrc}-${retryKey}`}
        src={currentSrc}
        alt="Message image"
        className={styles.image}
        onError={handleError}
        onLoad={handleLoad}
        preview={!disablePreview}
        style={{
          width: "100%",
          display: "block",
          height: "100%",
          pointerEvents: disablePreview ? "none" : "auto",
        }}
      />
    </div>
  );
};

interface FacebookImageGridProps {
  images: Array<{
    url: string;
    attachment?: {
      type: string;
      payload?: { url?: string };
      url?: string;
    };
  }>;
  messageId?: string;
  conversationId?: string;
  pageId?: string | null;
}

export const FacebookImageGrid = ({
  images,
  messageId,
  conversationId,
  pageId,
}: FacebookImageGridProps) => {
  const validImages = images.filter((img) => img.url);
  const count = validImages.length;
  const [previewVisible, setPreviewVisible] = useState(false);

  if (count === 0) return null;
  if (count === 1) {
    return (
      <MessageImage
        src={validImages[0].url}
        messageId={messageId}
        conversationId={conversationId}
        pageId={pageId}
      />
    );
  }

  const imagesToShow = validImages.slice(0, 5);
  const remainingCount = count - 5;

  const handleStackClick = () => {
    setPreviewVisible(true);
  };

  return (
    <>
      <div
        className={styles.fbImageStack}
        onClick={handleStackClick}
        style={{ cursor: "pointer" }}
      >
        {imagesToShow.map((img, index) => (
          <div key={index} className={styles.fbImageStackItem}>
            <MessageImage
              src={img.url}
              messageId={messageId}
              conversationId={conversationId}
              pageId={pageId}
              disablePreview={true}
            />
            {index === 4 && remainingCount > 0 && (
              <div className={styles.fbStackOverlay}>+{remainingCount}</div>
            )}
          </div>
        ))}
      </div>
      <ImagePreviewModal
        images={validImages}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
      />
    </>
  );
};
